import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ProfilePhoto } from '../components/ProfilePhoto';
import { toast } from 'sonner';
import { 
  Users, GraduationCap, BookOpen, Shield, Check, X, Pause, Play, 
  Trash2, Edit, Eye, Search, RefreshCw, CheckSquare, KeyRound, Mail, Copy, Link2
} from 'lucide-react';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfessor, setSelectedProfessor] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, user: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, type: 'user' });
  
  // Bulk selection states
  const [selectedProfessors, setSelectedProfessors] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState({ open: false, type: '', ids: [] });
  const [processing, setProcessing] = useState(false);
  const [resetLinkDialog, setResetLinkDialog] = useState({ open: false, url: '', email: '', userName: '' });
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, profsRes, studsRes, classesRes] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getProfessors(),
        adminAPI.getStudents(),
        adminAPI.getClasses()
      ]);
      setStats(statsRes.data);
      setProfessors(profsRes.data);
      setStudents(studsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error('Error cargando datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Individual actions - update state locally instead of full reload
  const handleApprove = async (userId) => {
    setProcessing(true);
    try {
      await adminAPI.approveUser(userId);
      setProfessors(prev => prev.map(p => p.id === userId ? {...p, status: 'active'} : p));
      setStats(prev => prev ? {...prev, pending_professors: prev.pending_professors - 1, active_professors: prev.active_professors + 1} : prev);
      toast.success('Profesor aprobado');
    } catch (error) {
      toast.error('Error al aprobar');
    } finally {
      setProcessing(false);
    }
  };

  const handlePause = async (userId) => {
    setProcessing(true);
    try {
      await adminAPI.pauseUser(userId);
      // Update local state
      setProfessors(prev => prev.map(p => p.id === userId ? {...p, status: 'paused'} : p));
      setStudents(prev => prev.map(s => s.id === userId ? {...s, status: 'paused'} : s));
      toast.success('Usuario pausado');
    } catch (error) {
      toast.error('Error al pausar');
    } finally {
      setProcessing(false);
    }
  };

  const handleActivate = async (userId) => {
    setProcessing(true);
    try {
      await adminAPI.activateUser(userId);
      setProfessors(prev => prev.map(p => p.id === userId ? {...p, status: 'active'} : p));
      setStudents(prev => prev.map(s => s.id === userId ? {...s, status: 'active'} : s));
      toast.success('Usuario reactivado');
    } catch (error) {
      toast.error('Error al reactivar');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.user) return;
    setProcessing(true);
    try {
      if (deleteDialog.type === 'class') {
        await adminAPI.deleteClass(deleteDialog.user.id);
        setClasses(prev => prev.filter(c => c.id !== deleteDialog.user.id));
        setStats(prev => prev ? {...prev, total_classes: prev.total_classes - 1} : prev);
        toast.success('Clase eliminada');
      } else {
        await adminAPI.deleteUser(deleteDialog.user.id);
        if (deleteDialog.user.role === 'professor') {
          setProfessors(prev => prev.filter(p => p.id !== deleteDialog.user.id));
          setStats(prev => prev ? {...prev, total_professors: prev.total_professors - 1} : prev);
        } else {
          setStudents(prev => prev.filter(s => s.id !== deleteDialog.user.id));
          setStats(prev => prev ? {...prev, total_students: prev.total_students - 1} : prev);
        }
        toast.success('Usuario eliminado');
      }
      setDeleteDialog({ open: false, user: null, type: 'user' });
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!editDialog.user) return;
    setProcessing(true);
    const emailChanged = editDialog.user._originalEmail && editDialog.user._originalEmail !== editDialog.user.email;
    try {
      await adminAPI.updateUser(editDialog.user.id, {
        name: editDialog.user.name,
        email: editDialog.user.email
      });
      // Update local state
      setProfessors(prev => prev.map(p => p.id === editDialog.user.id ? {...p, name: editDialog.user.name, email: editDialog.user.email} : p));
      setStudents(prev => prev.map(s => s.id === editDialog.user.id ? {...s, name: editDialog.user.name, email: editDialog.user.email} : s));
      toast.success('Usuario actualizado');
      setEditDialog({ open: false, user: null });

      // If email changed, prompt to send access email
      if (emailChanged) {
        handleSendAccessEmail(editDialog.user.id, editDialog.user.name);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendResetLink = async (userId, userName) => {
    setProcessing(true);
    try {
      const res = await adminAPI.sendResetLink(userId);
      setResetLinkDialog({
        open: true,
        url: res.data.reset_url,
        email: res.data.email,
        userName: userName
      });
      toast.success('Enlace de reseteo generado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al generar enlace');
    } finally {
      setProcessing(false);
    }
  };

  const handleSendAccessEmail = async (userId, userName) => {
    setProcessing(true);
    try {
      const res = await adminAPI.sendAccessEmail(userId);
      setResetLinkDialog({
        open: true,
        url: res.data.reset_url,
        email: res.data.email,
        userName: userName
      });
      toast.success('Correo de acceso generado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar correo');
    } finally {
      setProcessing(false);
    }
  };

  const copyResetLink = () => {
    navigator.clipboard.writeText(resetLinkDialog.url);
    setCopiedLink(true);
    toast.success('Enlace copiado');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  // Bulk actions
  const handleBulkDelete = async () => {
    setProcessing(true);
    try {
      if (bulkDeleteDialog.type === 'professors' || bulkDeleteDialog.type === 'students') {
        const result = await adminAPI.bulkDeleteUsers(bulkDeleteDialog.ids);
        if (bulkDeleteDialog.type === 'professors') {
          setProfessors(prev => prev.filter(p => !bulkDeleteDialog.ids.includes(p.id)));
          setSelectedProfessors([]);
        } else {
          setStudents(prev => prev.filter(s => !bulkDeleteDialog.ids.includes(s.id)));
          setSelectedStudents([]);
        }
        toast.success(result.data.message);
        if (result.data.errors?.length > 0) {
          result.data.errors.forEach(err => toast.error(err));
        }
      } else if (bulkDeleteDialog.type === 'classes') {
        const result = await adminAPI.bulkDeleteClasses(bulkDeleteDialog.ids);
        setClasses(prev => prev.filter(c => !bulkDeleteDialog.ids.includes(c.id)));
        setSelectedClasses([]);
        toast.success(result.data.message);
      }
      setBulkDeleteDialog({ open: false, type: '', ids: [] });
      // Refresh stats
      const statsRes = await adminAPI.getStats();
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Error en operación');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkAction = async (action) => {
    const ids = [...selectedProfessors, ...selectedStudents];
    if (ids.length === 0) {
      toast.error('Selecciona al menos un usuario');
      return;
    }
    setProcessing(true);
    try {
      const result = await adminAPI.bulkActionUsers(ids, action);
      const newStatus = action === 'pause' ? 'paused' : 'active';
      setProfessors(prev => prev.map(p => ids.includes(p.id) ? {...p, status: newStatus} : p));
      setStudents(prev => prev.map(s => ids.includes(s.id) ? {...s, status: newStatus} : s));
      setSelectedProfessors([]);
      setSelectedStudents([]);
      toast.success(result.data.message);
    } catch (error) {
      toast.error('Error en operación');
    } finally {
      setProcessing(false);
    }
  };

  const loadClassStudents = async (classId) => {
    try {
      const { data } = await adminAPI.getClassStudents(classId);
      setClassStudents(data);
    } catch (error) {
      toast.error('Error cargando estudiantes');
    }
  };

  // Toggle selection
  const toggleProfessorSelection = (profId) => {
    setSelectedProfessors(prev => 
      prev.includes(profId) ? prev.filter(id => id !== profId) : [...prev, profId]
    );
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev => 
      prev.includes(studentId) ? prev.filter(id => id !== studentId) : [...prev, studentId]
    );
  };

  const toggleClassSelection = (classId) => {
    setSelectedClasses(prev => 
      prev.includes(classId) ? prev.filter(id => id !== classId) : [...prev, classId]
    );
  };

  const toggleAllProfessors = () => {
    if (selectedProfessors.length === filteredProfessors.length) {
      setSelectedProfessors([]);
    } else {
      setSelectedProfessors(filteredProfessors.map(p => p.id));
    }
  };

  const toggleAllStudents = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map(s => s.id));
    }
  };

  const toggleAllClasses = () => {
    if (selectedClasses.length === classes.length) {
      setSelectedClasses([]);
    } else {
      setSelectedClasses(classes.map(c => c.id));
    }
  };

  const filteredProfessors = professors.filter(p => {
    const matchesStatus = filterStatus === 'all' || p.status === filterStatus;
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         p.email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredStudents = students.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      active: { variant: 'default', label: 'Activo', className: 'bg-green-500' },
      pending: { variant: 'default', label: 'Pendiente', className: 'bg-yellow-500' },
      paused: { variant: 'default', label: 'Pausado', className: 'bg-red-500' }
    };
    const config = variants[status] || variants.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center gap-3 mb-8">
          <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 flex-shrink-0" />
          <div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-slate-900">Panel de Administración</h2>
            <p className="text-sm text-slate-500 mt-0.5">Gestiona profesores, estudiantes y clases</p>
          </div>
        </div>
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold">{stats?.total_professors || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Profesores</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg flex-shrink-0">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold">{stats?.pending_professors || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-green-100 rounded-lg flex-shrink-0">
                  <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold">{stats?.total_students || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Estudiantes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 sm:pt-6">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="p-2 sm:p-3 bg-purple-100 rounded-lg flex-shrink-0">
                  <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-xl sm:text-2xl font-bold">{stats?.total_classes || 0}</p>
                  <p className="text-xs sm:text-sm text-slate-500">Clases</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs defaultValue="professors" className="space-y-4">
          <TabsList>
            <TabsTrigger value="professors" data-testid="tab-professors">
              <Users className="w-4 h-4 mr-2" />
              Profesores
            </TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">
              <GraduationCap className="w-4 h-4 mr-2" />
              Estudiantes
            </TabsTrigger>
            <TabsTrigger value="classes" data-testid="tab-classes">
              <BookOpen className="w-4 h-4 mr-2" />
              Clases
            </TabsTrigger>
          </TabsList>

          {/* Professors Tab */}
          <TabsContent value="professors">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Gestión de Profesores</CardTitle>
                    <CardDescription>Aprueba, pausa o elimina profesores</CardDescription>
                  </div>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <div className="relative flex-1 sm:flex-none">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Buscar..." 
                        className="pl-9 w-full sm:w-64"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="search-professors"
                      />
                    </div>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger className="w-full sm:w-40" data-testid="filter-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="pending">Pendientes</SelectItem>
                        <SelectItem value="active">Activos</SelectItem>
                        <SelectItem value="paused">Pausados</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Bulk actions bar */}
                {selectedProfessors.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">{selectedProfessors.length} seleccionados</span>
                    <div className="flex-1 min-w-0" />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('pause')} disabled={processing}>
                        <Pause className="w-4 h-4 mr-1" /> Pausar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('activate')} disabled={processing}>
                        <Play className="w-4 h-4 mr-1" /> Activar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => setBulkDeleteDialog({ open: true, type: 'professors', ids: selectedProfessors })}
                        disabled={processing}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedProfessors([])}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Select all header */}
                  <div className="flex items-center gap-3 pb-2 border-b">
                    <Checkbox 
                      checked={selectedProfessors.length === filteredProfessors.length && filteredProfessors.length > 0}
                      onCheckedChange={toggleAllProfessors}
                    />
                    <span className="text-sm text-slate-500">Seleccionar todos</span>
                  </div>
                  
                  {filteredProfessors.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No se encontraron profesores</p>
                  ) : (
                    filteredProfessors.map(prof => (
                      <div 
                        key={prof.id} 
                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 rounded-lg transition-colors ${selectedProfessors.includes(prof.id) ? 'bg-blue-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                        data-testid={`professor-row-${prof.id}`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <Checkbox 
                            checked={selectedProfessors.includes(prof.id)}
                            onCheckedChange={() => toggleProfessorSelection(prof.id)}
                          />
                          <ProfilePhoto photoUrl={prof.photo_url} name={prof.name} size={40} />
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">{prof.name}</p>
                            <p className="text-xs sm:text-sm text-slate-500 truncate">{prof.email}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-slate-400">{prof.classes_count} clases</span>
                              <span className="text-xs text-slate-400">·</span>
                              <span className="text-xs text-slate-400">{prof.students_count} estudiantes</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 ml-10 sm:ml-0">
                          {getStatusBadge(prof.status)}
                          <div className="flex items-center gap-1">
                            {prof.status === 'pending' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleApprove(prof.id)}
                                className="bg-green-600 hover:bg-green-700"
                                disabled={processing}
                                data-testid={`approve-${prof.id}`}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            {prof.status === 'active' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handlePause(prof.id)}
                                disabled={processing}
                                data-testid={`pause-${prof.id}`}
                              >
                                <Pause className="w-4 h-4" />
                              </Button>
                            )}
                            {prof.status === 'paused' && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleActivate(prof.id)}
                                disabled={processing}
                                data-testid={`activate-${prof.id}`}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEditDialog({ open: true, user: { ...prof, _originalEmail: prof.email } })}
                              data-testid={`edit-${prof.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              onClick={() => handleSendResetLink(prof.id, prof.name)}
                              disabled={processing}
                              data-testid={`reset-link-${prof.id}`}
                              title="Enviar enlace de reseteo de contraseña"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => setDeleteDialog({ open: true, user: prof, type: 'user' })}
                              data-testid={`delete-${prof.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Gestión de Estudiantes</CardTitle>
                    <CardDescription>Administra las cuentas de estudiantes</CardDescription>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input 
                      placeholder="Buscar..." 
                      className="pl-9 w-full sm:w-64"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="search-students"
                    />
                  </div>
                </div>
                
                {/* Bulk actions bar */}
                {selectedStudents.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">{selectedStudents.length} seleccionados</span>
                    <div className="flex-1 min-w-0" />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('pause')} disabled={processing}>
                        <Pause className="w-4 h-4 mr-1" /> Pausar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleBulkAction('activate')} disabled={processing}>
                        <Play className="w-4 h-4 mr-1" /> Activar
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => setBulkDeleteDialog({ open: true, type: 'students', ids: selectedStudents })}
                        disabled={processing}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setSelectedStudents([])}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Select all header */}
                  <div className="flex items-center gap-3 pb-2 border-b">
                    <Checkbox 
                      checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                      onCheckedChange={toggleAllStudents}
                    />
                    <span className="text-sm text-slate-500">Seleccionar todos</span>
                  </div>
                  
                  {filteredStudents.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No se encontraron estudiantes</p>
                  ) : (
                    filteredStudents.map(student => (
                      <div 
                        key={student.id} 
                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 rounded-lg transition-colors ${selectedStudents.includes(student.id) ? 'bg-blue-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                        data-testid={`student-row-${student.id}`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <Checkbox 
                            checked={selectedStudents.includes(student.id)}
                            onCheckedChange={() => toggleStudentSelection(student.id)}
                          />
                          <ProfilePhoto photoUrl={student.photo_url} name={student.name} size={40} />
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">{student.name}</p>
                            <p className="text-xs sm:text-sm text-slate-500 truncate">{student.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3 ml-10 sm:ml-0">
                          {getStatusBadge(student.status)}
                          <div className="flex items-center gap-1">
                            {student.status === 'active' ? (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handlePause(student.id)}
                                disabled={processing}
                              >
                                <Pause className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleActivate(student.id)}
                                disabled={processing}
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setEditDialog({ open: true, user: { ...student, _originalEmail: student.email } })}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                              onClick={() => handleSendResetLink(student.id, student.name)}
                              disabled={processing}
                              data-testid={`reset-link-${student.id}`}
                              title="Enviar enlace de reseteo de contraseña"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => setDeleteDialog({ open: true, user: student, type: 'user' })}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle>Todas las Clases</CardTitle>
                    <CardDescription>Vista general de clases en el sistema</CardDescription>
                  </div>
                </div>
                
                {/* Bulk actions bar */}
                {selectedClasses.length > 0 && (
                  <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
                    <CheckSquare className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">{selectedClasses.length} seleccionadas</span>
                    <div className="flex-1" />
                    <Button 
                      size="sm" 
                      variant="destructive" 
                      onClick={() => setBulkDeleteDialog({ open: true, type: 'classes', ids: selectedClasses })}
                      disabled={processing}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedClasses([])}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Select all header */}
                  <div className="flex items-center gap-3 pb-2 border-b">
                    <Checkbox 
                      checked={selectedClasses.length === classes.length && classes.length > 0}
                      onCheckedChange={toggleAllClasses}
                    />
                    <span className="text-sm text-slate-500">Seleccionar todas</span>
                  </div>
                  
                  {classes.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No hay clases registradas</p>
                  ) : (
                    classes.map(cls => (
                      <div 
                        key={cls.id} 
                        className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 rounded-lg transition-colors ${selectedClasses.includes(cls.id) ? 'bg-blue-50' : 'bg-slate-50 hover:bg-slate-100'}`}
                        data-testid={`class-row-${cls.id}`}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <Checkbox 
                            checked={selectedClasses.includes(cls.id)}
                            onCheckedChange={() => toggleClassSelection(cls.id)}
                          />
                          <div className="min-w-0">
                            <p className="font-medium text-sm sm:text-base truncate">{cls.name}</p>
                            <p className="text-xs sm:text-sm text-slate-500 truncate">
                              Profesor: {cls.professor_name} · {cls.students_count} estudiantes
                            </p>
                            <p className="text-xs text-slate-400">{cls.semester}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-8 sm:ml-0">
                          <Badge variant="outline">{cls.join_code}</Badge>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setSelectedClass(cls);
                              loadClassStudents(cls.id);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => setDeleteDialog({ open: true, user: cls, type: 'class' })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Class Students Dialog */}
        <Dialog open={!!selectedClass} onOpenChange={() => setSelectedClass(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Estudiantes de {selectedClass?.name}</DialogTitle>
              <DialogDescription>
                Profesor: {selectedClass?.professor_name} • Código: {selectedClass?.join_code}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 mt-4">
              {classStudents.length === 0 ? (
                <p className="text-center text-slate-500 py-4">No hay estudiantes inscritos</p>
              ) : (
                classStudents.map(student => (
                  <div key={student.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <ProfilePhoto photoUrl={student.photo_url} name={student.name} size={40} />
                      <div>
                        <p className="font-medium">{student.name}</p>
                        <p className="text-sm text-slate-500">{student.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <p className="font-bold text-yellow-600">{student.balance}</p>
                        <p className="text-xs text-slate-400">Coins</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-blue-600">{student.total_xp}</p>
                        <p className="text-xs text-slate-400">XP</p>
                      </div>
                      <div className="text-center">
                        <p className="font-bold text-purple-600">Nv.{student.level}</p>
                        <p className="text-xs text-slate-400">Nivel</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialog.open} onOpenChange={(open) => !open && setEditDialog({ open: false, user: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
              <DialogDescription>Modifica los datos del usuario</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input 
                  value={editDialog.user?.name || ''} 
                  onChange={(e) => setEditDialog(prev => ({ 
                    ...prev, 
                    user: { ...prev.user, name: e.target.value } 
                  }))}
                  data-testid="edit-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input 
                  value={editDialog.user?.email || ''} 
                  onChange={(e) => setEditDialog(prev => ({ 
                    ...prev, 
                    user: { ...prev.user, email: e.target.value } 
                  }))}
                  data-testid="edit-email"
                />
                {editDialog.user?._originalEmail && editDialog.user._originalEmail !== editDialog.user.email && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    Al guardar, se generará un enlace de acceso para el nuevo correo
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 sm:mr-auto"
                onClick={() => { handleSendResetLink(editDialog.user.id, editDialog.user.name); setEditDialog({ open: false, user: null }); }}
                disabled={processing}
                data-testid="send-reset-from-edit"
              >
                <KeyRound className="w-4 h-4 mr-1.5" />
                Resetear Contraseña
              </Button>
              <Button variant="outline" onClick={() => setEditDialog({ open: false, user: null })}>
                Cancelar
              </Button>
              <Button onClick={handleUpdate} disabled={processing} data-testid="save-edit">
                {processing ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null, type: 'user' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminar {deleteDialog.type === 'class' ? 'Clase' : 'Usuario'}</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de eliminar {deleteDialog.type === 'class' ? 'la clase' : 'a'} {deleteDialog.user?.name}? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, user: null, type: 'user' })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete} disabled={processing} data-testid="confirm-delete">
                {processing ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Dialog */}
        <Dialog open={bulkDeleteDialog.open} onOpenChange={(open) => !open && setBulkDeleteDialog({ open: false, type: '', ids: [] })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Eliminación en Lote</DialogTitle>
              <DialogDescription>
                ¿Estás seguro de eliminar {bulkDeleteDialog.ids.length} {bulkDeleteDialog.type === 'classes' ? 'clases' : 'usuarios'}? Esta acción no se puede deshacer.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteDialog({ open: false, type: '', ids: [] })}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={processing} data-testid="confirm-bulk-delete">
                {processing ? 'Eliminando...' : `Eliminar ${bulkDeleteDialog.ids.length} elementos`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Link Dialog */}
        <Dialog open={resetLinkDialog.open} onOpenChange={(open) => { if (!open) { setResetLinkDialog({ open: false, url: '', email: '', userName: '' }); setCopiedLink(false); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-indigo-600" />
                Enlace de Acceso Generado
              </DialogTitle>
              <DialogDescription>
                Enlace generado para <strong>{resetLinkDialog.userName}</strong> ({resetLinkDialog.email})
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium flex items-center gap-1.5">
                  <Mail className="w-4 h-4" />
                  El correo se intentó enviar automáticamente
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Si el servicio de correo no está activo, comparte el enlace manualmente:
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs font-mono text-slate-700 break-all select-all" data-testid="reset-link-url">
                  {resetLinkDialog.url}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  className="flex-shrink-0 h-10 w-10"
                  onClick={copyResetLink}
                  data-testid="copy-reset-link-btn"
                >
                  {copiedLink ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Este enlace expira en 1 hora. El usuario podrá establecer una nueva contraseña.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => { setResetLinkDialog({ open: false, url: '', email: '', userName: '' }); setCopiedLink(false); }}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
